import React from "react";
import { Group, Panel, Separator } from "react-resizable-panels";
import { MAIL_PANELS, useMailPanelLayout } from "./mail-panel-layout";
import type { MessageView } from "@/api";
import { Icon } from "@/lib";
import type { EvidenceMark } from "../evidence-highlight";
import { CustomerMailRail } from "./customer-mail-rail";
import { SelectedMessagePane } from "./selected-message-pane";
import { ReviewTabs } from "./review-tabs";
import { reviewMessageEntries, type ReviewTabId } from "./model";
import { useT } from "../../i18n";
import styles from "./email-review.module.css";

type MobilePane = "message" | "review";

const PHONE_QUERY = "(max-width: 580px)";
const DRAWER_FOCUSABLE = [
  "button:not(:disabled)",
  "input:not(:disabled)",
  "select:not(:disabled)",
  "textarea:not(:disabled)",
  "a[href]",
  "summary",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function mediaQueryMatches(query: string): boolean {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia(query).matches;
}

function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback((onChange: () => void) => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => undefined;
    const media = window.matchMedia(query);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  const snapshot = React.useCallback(() => mediaQueryMatches(query), [query]);
  return React.useSyncExternalStore(subscribe, snapshot, () => false);
}

/** The sidebar changes available space without changing the viewport. Keep pane identity stable. */
function useAdaptiveWorkspace(surfaceRef: React.RefObject<HTMLDivElement | null>): boolean {
  const [adaptive, setAdaptive] = React.useState(true);
  React.useLayoutEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;
    const measure = () => setAdaptive(surface.getBoundingClientRect().width <= 1120);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(surface);
    return () => observer.disconnect();
  }, [surfaceRef]);
  return adaptive;
}

function scrollOwner(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;
  // #2250: the shell's area below the navigation bar scrolls on a desktop; a phone scrolls the window.
  for (const owner of [element.closest<HTMLElement>(".topshell__scroll"), element.closest<HTMLElement>(".app-root")]) {
    if (owner && /(auto|scroll)/.test(getComputedStyle(owner).overflowY)) return owner;
  }
  return document.scrollingElement as HTMLElement | null;
}

export function EmailReviewWorkspace({
  userId = null,
  requestId,
  unconfirmedForm = false,
  onOpenRequest,
  messages,
  selectedMessageKey,
  unreadMessageKey,
  onSelectMessage,
  conversationActions,
  messageActions,
  marks,
  activeMark,
  onMarkHover,
  activeTab,
  onTabChange,
  editing,
  panels,
  guidance,
  requiredCount,
  actions,
}: {
  userId?: string | null;
  /** The request whose attachments the selected message may link to (#594). */
  requestId: string;
  unconfirmedForm?: boolean;
  onOpenRequest: (requestId: string) => void;
  messages: MessageView[];
  selectedMessageKey: string | null;
  unreadMessageKey: string | null;
  onSelectMessage: (key: string) => void;
  conversationActions?: React.ReactNode;
  messageActions?: (message: MessageView) => React.ReactNode;
  marks: EvidenceMark[];
  activeMark: string | null;
  onMarkHover: (key: string | null) => void;
  activeTab: ReviewTabId;
  onTabChange: (tab: ReviewTabId) => void;
  editing: boolean;
  panels: Record<ReviewTabId, React.ReactNode>;
  guidance?: React.ReactNode;
  requiredCount?: number | null;
  actions: React.ReactNode;
}) {
  const t = useT();
  const surfaceRef = React.useRef<HTMLDivElement>(null);
  const adaptive = useAdaptiveWorkspace(surfaceRef);
  const phone = useMediaQuery(PHONE_QUERY);
  const layout = useMailPanelLayout(adaptive, userId);
  const restoreListRef = React.useRef<HTMLButtonElement>(null);
  const adaptiveOpenerRef = React.useRef<HTMLButtonElement>(null);
  const lastFocusRef = React.useRef<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [mobilePane, setMobilePane] = React.useState<MobilePane>("message");
  const drawerRef = React.useRef<HTMLElement>(null);
  const drawerOpenerRef = React.useRef<HTMLButtonElement | null>(null);
  const drawerLastFocusRef = React.useRef<HTMLElement | null>(null);
  const restoreDrawerFocusRef = React.useRef(false);
  const messageStageRef = React.useRef<HTMLDivElement>(null);
  const reviewStageRef = React.useRef<HTMLDivElement>(null);
  const paneFocusRef = React.useRef<Record<MobilePane, HTMLElement | null>>({ message: null, review: null });
  const paneScrollRef = React.useRef<Record<MobilePane, number>>({ message: 0, review: 0 });
  const pendingPaneFocusRef = React.useRef<MobilePane | null>(null);
  const entries = React.useMemo(() => reviewMessageEntries(messages), [messages]);
  const selected = entries.find((entry) => entry.key === selectedMessageKey) ?? entries[0] ?? null;
  React.useLayoutEffect(() => {
    const surface = surfaceRef.current;
    if (!surface || phone || activeTab !== "draft") return;
    const values = [...surface.querySelectorAll<HTMLElement>("[data-mail-address-content]")];
    const publish = () => {
      for (const field of ["from", "to"]) {
        const height = Math.max(0, ...values
          .filter((node) => node.dataset.mailAddressContent === field)
          .map((node) => node.getBoundingClientRect().height));
        const property = `--mail-${field}-height`;
        const next = `${height}px`;
        if (surface.style.getPropertyValue(property) !== next) surface.style.setProperty(property, next);
      }
    };
    // Measure natural text spans, not stretched grid cells, so rows can shrink again.
    publish();
    const observer = new ResizeObserver(publish);
    values.forEach((node) => observer.observe(node));
    return () => {
      observer.disconnect();
      surface.style.removeProperty("--mail-from-height");
      surface.style.removeProperty("--mail-to-height");
    };
  }, [activeTab, phone, selected?.key, panels.draft]);
  // Entries are newest-first, so the thread's earlier messages sit after the selected
  // one; their bodies feed the repeated-history detection (#533).
  const earlierBodies = React.useMemo(() => {
    if (!selected) return [];
    const position = entries.indexOf(selected);
    return entries.slice(position + 1).map((entry) => entry.message.normalizedText || "");
  }, [entries, selected]);
  const selectedIndex = selected ? entries.indexOf(selected) : -1;
  const messageNavigation = entries.length > 1 && selectedIndex >= 0 ? (
    <div className={styles.messageStepper} aria-label={t("ws.review.messageNavigation")}>
      <button
        type="button"
        className="iconbtn"
        aria-label={t("ws.review.newerMessage")}
        disabled={selectedIndex === 0}
        onClick={() => onSelectMessage(entries[selectedIndex - 1]!.key)}
      >
        <Icon name="chevronLeft" size={15} />
      </button>
      <span>{t("ws.review.messagePosition", { current: selectedIndex + 1, total: entries.length })}</span>
      <button
        type="button"
        className="iconbtn"
        aria-label={t("ws.review.olderMessage")}
        disabled={selectedIndex === entries.length - 1}
        onClick={() => onSelectMessage(entries[selectedIndex + 1]!.key)}
      >
        <Icon name="chevronRight" size={15} />
      </button>
    </div>
  ) : null;

  const rememberDrawerFocus = React.useCallback(() => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && drawerRef.current?.contains(active)) drawerLastFocusRef.current = active;
  }, []);

  const closeDrawer = React.useCallback(() => {
    if (!drawerOpen) return;
    rememberDrawerFocus();
    restoreDrawerFocusRef.current = true;
    setDrawerOpen(false);
  }, [drawerOpen, rememberDrawerFocus]);

  const openDrawer = React.useCallback((opener: HTMLButtonElement) => {
    drawerOpenerRef.current = opener;
    restoreDrawerFocusRef.current = false;
    setDrawerOpen(true);
  }, []);

  React.useLayoutEffect(() => {
    if (!adaptive) {
      if (surfaceRef.current) surfaceRef.current.inert = false;
      return;
    }
    if (surfaceRef.current) surfaceRef.current.inert = drawerOpen;
    if (drawerRef.current) drawerRef.current.inert = !drawerOpen;
    if (drawerOpen) {
      const target = drawerLastFocusRef.current?.isConnected
        ? drawerLastFocusRef.current
        : drawerRef.current?.querySelector<HTMLElement>("[data-customer-mail-key][aria-current='true']")
          ?? drawerRef.current?.querySelector<HTMLElement>("[data-customer-mail-key]");
      target?.focus({ preventScroll: true });
    } else if (restoreDrawerFocusRef.current) {
      restoreDrawerFocusRef.current = false;
      const opener = drawerOpenerRef.current;
      window.requestAnimationFrame(() => {
        if (opener?.isConnected) opener.focus({ preventScroll: true });
      });
    }
  }, [adaptive, drawerOpen]);

  React.useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const escapeOwner = event.target instanceof Element
          ? event.target.closest('[role="dialog"], [role="menu"]')
          : null;
        if (escapeOwner && escapeOwner !== drawerRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        closeDrawer();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...(drawerRef.current?.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE) ?? [])]
        .filter((element) => !element.hidden && !element.closest("[hidden]") && element.getClientRects().length > 0);
      if (focusable.length === 0) {
        event.preventDefault();
        drawerRef.current?.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [closeDrawer, drawerOpen]);

  React.useLayoutEffect(() => {
    if (messageStageRef.current) messageStageRef.current.inert = phone && mobilePane !== "message";
    if (reviewStageRef.current) reviewStageRef.current.inert = phone && mobilePane !== "review";
    const pending = pendingPaneFocusRef.current;
    if (!phone || !pending) return;
    pendingPaneFocusRef.current = null;
    const stage = pending === "message" ? messageStageRef.current : reviewStageRef.current;
    const remembered = paneFocusRef.current[pending];
    const rememberedTarget = remembered?.isConnected && stage?.contains(remembered) ? remembered : null;
    const target = rememberedTarget ?? stage;
    target?.focus({ preventScroll: true });
    const owner = scrollOwner(stage);
    if (owner) owner.scrollTop = paneScrollRef.current[pending];
    const frame = window.requestAnimationFrame(() => {
      if (!rememberedTarget || document.activeElement !== rememberedTarget || !stage || !owner) return;
      const actionBar = stage.querySelector<HTMLElement>('[data-testid="review-actions"]');
      if (!actionBar) return;
      const targetRect = rememberedTarget.getBoundingClientRect();
      const actionRect = actionBar.getBoundingClientRect();
      const overlap = targetRect.bottom - actionRect.top + 12;
      if (overlap > 0) owner.scrollTop += overlap;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [mobilePane, phone]);

  React.useEffect(() => {
    if (!adaptive && drawerOpen) setDrawerOpen(false);
  }, [adaptive, drawerOpen]);

  const showMobilePane = React.useCallback((next: MobilePane) => {
    if (!phone || next === mobilePane) return;
    const currentStage = mobilePane === "message" ? messageStageRef.current : reviewStageRef.current;
    const owner = scrollOwner(currentStage);
    if (owner) paneScrollRef.current[mobilePane] = owner.scrollTop;
    pendingPaneFocusRef.current = next;
    setMobilePane(next);
  }, [mobilePane, phone]);

  /**
   * On a phone the review panels live in a pane of their own. A tab selected from outside this
   * component — first seen with the retired presentation tour (#583) — therefore used to change
   * a tab nobody could see: the panel stayed hidden (#1056). Follow the
   * tab to the pane that shows it. A tab the operator picked themselves is on that pane already,
   * so this only moves the screen when something else asked for the tab.
   */
  const shownTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    if (shownTabRef.current === activeTab) return;
    shownTabRef.current = activeTab;
    showMobilePane("review");
  }, [activeTab, showMobilePane]);

  React.useLayoutEffect(() => {
    if (layout.listRef.current) layout.listRef.current.inert = adaptive || layout.collapsed;
    const focused = lastFocusRef.current;
    const focusedPane = focused && messageStageRef.current?.contains(focused) ? "message"
      : focused && reviewStageRef.current?.contains(focused) ? "review" : null;
    if (phone && focusedPane && mobilePane !== focusedPane) {
      pendingPaneFocusRef.current = focusedPane;
      setMobilePane(focusedPane);
      return;
    }
    if (!focused || (focused.isConnected && focused.getClientRects().length > 0
      && !focused.closest('[inert]'))) return;
    const target = adaptive ? adaptiveOpenerRef.current : restoreListRef.current;
    target?.focus({ preventScroll: true });
  }, [adaptive, phone, layout.collapsed, layout.listRef]);

  /**
   * #1284: one control for both directions, on the strip that resizes the list — hiding and
   * restoring are the same act and now happen in the same place.
   *
   * It is a sibling of the separator rather than its child, because `role="separator"` takes
   * presentational children and would hide this button from assistive technology. A zero-width
   * flex item anchors it, so the strip's own geometry places the handle without measuring
   * anything.
   *
   * The handle overlaps the separator, which is inside the resize group, so the library would
   * otherwise read a press on it as the start of a drag — moving focus onto the separator and out
   * of an open editor. A capture listener on `window` always runs before the library's own on
   * `document`, so cancelling the press there is what keeps it an ordinary button press. The
   * cancellation also suppresses the compatibility mouse events, so a press moves no focus at all
   * — which is why nothing here focuses the button either (#1291): a scripted focus after a
   * cancelled press is what the browser draws a focus ring for. Focus only lands here when the
   * effect above moves it, because the list it was in went inert.
   */
  React.useEffect(() => {
    if (adaptive) return;
    const claimHandlePress = (event: PointerEvent) => {
      if (event.target instanceof Node && restoreListRef.current?.contains(event.target)) {
        event.preventDefault();
      }
    };
    window.addEventListener("pointerdown", claimHandlePress, true);
    return () => window.removeEventListener("pointerdown", claimHandlePress, true);
  }, [adaptive]);

  const listToggle = (
    <div className={styles.listToggleAnchor}>
      <button
        ref={restoreListRef}
        type="button"
        className={styles.listToggle}
        aria-label={t(layout.collapsed ? "ws.review.restoreList" : "ws.review.collapseList")}
        title={t(layout.collapsed ? "ws.review.restoreList" : "ws.review.collapseList")}
        aria-expanded={!layout.collapsed}
        aria-controls={MAIL_PANELS.list}
        onClick={layout.toggleList}
      >
        <Icon name={layout.collapsed ? "chevronRight" : "chevronLeft"} size={16} />
      </button>
    </div>
  );

  const conversationRail = (
    <CustomerMailRail
      requestId={requestId}
      unconfirmedForm={unconfirmedForm}
      messages={messages}
      selectedMessageKey={selectedMessageKey}
      unreadMessageKey={unreadMessageKey}
      onSelectMessage={(key) => {
        if (adaptive) closeDrawer();
        onSelectMessage(key);
      }}
      onOpenRequest={(nextRequestId) => {
        if (adaptive) closeDrawer();
        onOpenRequest(nextRequestId);
      }}
      navigationDisabled={editing}
    />
  );

  return (
    <section
      className={styles.root}
      data-testid="email-review-workspace"
      onFocusCapture={(event) => { lastFocusRef.current = event.target as HTMLElement; }}
      onBlurCapture={(event) => {
        if (event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) lastFocusRef.current = null;
      }}
    >
      <div
        ref={surfaceRef}
        className={styles.workspaceSurface}
        data-testid="adaptive-workspace-surface"
        aria-hidden={adaptive && drawerOpen ? true : undefined}
      >
        {adaptive && (
          <nav className={styles.adaptiveBar} aria-label={t("ws.review.adaptiveNavigation")} data-testid="adaptive-workspace-nav">
            <button
              type="button"
              ref={adaptiveOpenerRef}
              className={styles.adaptiveNavButton}
              aria-label={t("ws.review.openConversations")}
              aria-haspopup="dialog"
              aria-expanded={drawerOpen}
              aria-controls="active-conversations-drawer"
              onClick={(event) => openDrawer(event.currentTarget)}
            >
              <Icon name="list" size={17} />
              <span>{t("ws.review.openConversations")}</span>
            </button>
            {phone && (
              <div className={styles.paneSwitcher} role="group" aria-label={t("ws.review.mobileFlow")}>
                <button
                  type="button"
                  aria-pressed={mobilePane === "message"}
                  onClick={() => showMobilePane("message")}
                >
                  <Icon name="mailOpen" size={16} />
                  <span>{t("ws.review.showMessage")}</span>
                </button>
                <button
                  type="button"
                  aria-pressed={mobilePane === "review"}
                  onClick={() => showMobilePane("review")}
                >
                  <span>{t("ws.review.showReview")}</span>
                  <Icon name="arrowRight" size={16} />
                </button>
              </div>
            )}
          </nav>
        )}
        <Group
          id="mail-panel-group"
          className={`${styles.grid}${!adaptive && layout.collapsed ? ` ${styles.gridCollapsed}` : ""}`}
          elementRef={layout.elementRef}
          groupRef={layout.groupRef}
          disabled={adaptive}
          onFocusCapture={layout.onSeparatorFocus}
          style={{ height: "auto", overflow: "clip", opacity: layout.ready ? undefined : 0 }}
          resizeTargetMinimumSize={{ fine: 12, coarse: 24 }}
          onLayoutChange={layout.onLayoutChange}
          onLayoutChanged={layout.onLayoutChanged}
          data-mobile-pane={phone ? mobilePane : undefined}
        >
          <Panel
            id={MAIL_PANELS.list}
            elementRef={layout.listRef}
            disabled={!adaptive && layout.resizeTarget === "review"}
            defaultSize={280} minSize={adaptive ? 0 : 240} maxSize={adaptive ? "100%" : 360}
            collapsible collapsedSize={0} groupResizeBehavior="preserve-pixel-size"
            hidden={adaptive || layout.collapsed} aria-hidden={adaptive || layout.collapsed || undefined}
            className={styles.railStage}
            style={{ overflow: "hidden" }}
          >
            {!adaptive && conversationRail}
          </Panel>
          <Separator
            id="mail-list-separator"
            className={styles.separator}
            disabled={adaptive}
            disableDoubleClick
            hidden={adaptive}
            onPointerEnter={(event) => {
              if (!event.buttons || event.pointerType !== "mouse") layout.setResizeTarget("list");
            }}
            onKeyDownCapture={(event) => {
              // v4 remembers a percentage for Enter; restore our last expanded pixel width.
              if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                layout.toggleList();
              }
            }}
            aria-label={t("ws.review.resizeList")}
          />
          {!adaptive && listToggle}
          <Panel
            id={MAIL_PANELS.source}
            minSize={adaptive ? 0 : 320}
            className={styles.contentPanel}
            style={{ overflow: "clip", maxHeight: "none" }}
          >
            <div
              ref={messageStageRef}
              className={styles.messageStage}
              data-testid="adaptive-message-stage"
              hidden={phone && mobilePane !== "message"}
              tabIndex={phone ? -1 : undefined}
              aria-label={phone ? t("ws.review.messagePane") : undefined}
              onFocusCapture={(event) => { paneFocusRef.current.message = event.target as HTMLElement; }}
            >
              <SelectedMessagePane
                requestId={requestId}
                message={selected?.message ?? null}
                messageKey={selected?.key ?? null}
                unread={selected?.key === unreadMessageKey}
                marks={marks}
                activeMark={activeMark}
                onMarkHover={onMarkHover}
                navigation={messageNavigation}
                actions={<>{conversationActions}{selected && messageActions?.(selected.message)}</>}
                earlierBodies={earlierBodies}
              />
            </div>
          </Panel>
          <Separator
            id="mail-review-separator"
            className={styles.separator}
            disabled={adaptive}
            disableDoubleClick
            hidden={adaptive}
            onPointerEnter={(event) => {
              if (!event.buttons || event.pointerType !== "mouse") layout.setResizeTarget("review");
            }}
            aria-label={t("ws.review.resizeReview")}
          />
          <Panel
            id={MAIL_PANELS.review}
            minSize={adaptive ? 0 : 420}
            disabled={!adaptive && layout.resizeTarget === "list"}
            className={styles.contentPanel}
            style={{ overflow: "clip", maxHeight: "none" }}
          >
            <div
              ref={reviewStageRef}
              className={styles.reviewStage}
              data-testid="adaptive-review-stage"
              hidden={phone && mobilePane !== "review"}
              tabIndex={phone ? -1 : undefined}
              aria-label={phone ? t("ws.review.reviewPane") : undefined}
              onFocusCapture={(event) => { paneFocusRef.current.review = event.target as HTMLElement; }}
            >
              <ReviewTabs requiredCount={requiredCount} activeTab={activeTab} onTabChange={onTabChange} editing={editing} />
              <section className={styles.detailPane} data-testid="review-detail-pane">
                <div
                  className={activeTab === "draft" ? styles.tabPanel : `${styles.tabPanel} ${styles.tabPanelInset}`}
                  role="tabpanel"
                  id={`review-panel-${activeTab}`}
                  aria-labelledby={`review-tab-${activeTab}`}
                >
                  {guidance}
                  {panels[activeTab]}
                </div>
                {actions}
              </section>
            </div>
          </Panel>
        </Group>
      </div>
      {adaptive && (
        <div
          className={styles.drawerLayer}
          data-testid="active-conversations-drawer-layer"
          data-open={drawerOpen ? "true" : "false"}
          aria-hidden={drawerOpen ? undefined : true}
        >
          <button
            type="button"
            className={styles.drawerBackdrop}
            data-testid="active-conversations-drawer-backdrop"
            tabIndex={-1}
            aria-label={t("ws.review.closeConversations")}
            onClick={closeDrawer}
          />
          <section
            ref={drawerRef}
            id="active-conversations-drawer"
            className={styles.drawerPanel}
            role="dialog"
            aria-modal="true"
            aria-labelledby="active-conversations-drawer-title"
            tabIndex={-1}
          >
            <header className={styles.drawerHeader}>
              <h2 id="active-conversations-drawer-title">{t(unconfirmedForm ? "ws.form.messagesTitle" : "ws.customerMail.title")}</h2>
              <button type="button" aria-label={t("ws.review.closeConversations")} onClick={closeDrawer}>
                <Icon name="x" size={18} />
              </button>
            </header>
            {conversationRail}
          </section>
        </div>
      )}
    </section>
  );
}
